const selectors = {
  accordion: '.js-accordion',
  accordionTrigger: '.js-accordion-trigger',
  accordionContent: '.js-accordion-content'
}

const classes = {
  active: 'active',
  initialized: 'initialized'
}

class CoreAccordion extends HTMLElement {
  constructor() {
    super();
    this.accordions = null
    this._resizeRaf = null
    this._onWindowResize = this.onWindowResize.bind(this)
    this._contentObservers = new Map()
    this._triggerHandlers = new Map()
  }

  connectedCallback() {
    this.accordions = this.querySelectorAll(selectors.accordion)

    if (!this.accordions.length) return
    this.initAccordion()
    window.addEventListener('resize', this._onWindowResize, { passive: true })
    this.initContentObservers()
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onWindowResize)
    if (this._resizeRaf) {
      cancelAnimationFrame(this._resizeRaf)
      this._resizeRaf = null
    }
    this.destroyTriggerHandlers()
    this.destroyContentObservers()
  }

  // Init accordion
  initAccordion() {
    this.accordions.forEach((accordion, index) => {
      const trigger = accordion.querySelector(selectors.accordionTrigger)
      const content = accordion.querySelector(selectors.accordionContent)
      if (trigger && content) {
        const existingHandler = this._triggerHandlers.get(trigger)
        if (existingHandler) {
          trigger.removeEventListener('click', existingHandler)
          this._triggerHandlers.delete(trigger)
        }

        const openFirst = this.getAttribute('data-open-first') === 'true'
        if (index === 0 && openFirst) {
          this.triggerAccordion(trigger, content)
        }
        const onClick = () => {
          const openMultiple = this.getAttribute('data-open-multiple') === 'true'
          if (!openMultiple) {
            const activeTrigger = this.querySelector(`${selectors.accordionTrigger}.${classes.active}`)
            if (activeTrigger && activeTrigger !== trigger) {
              activeTrigger.click()
            }
          }
          this.triggerAccordion(trigger, content)
        }
        trigger.addEventListener('click', onClick)
        this._triggerHandlers.set(trigger, onClick)
      }
    })
    this.classList.add(classes.initialized)
  }

  triggerAccordion(trigger, content) {
    trigger.classList.toggle(classes.active)
    if (content.style.maxHeight) {
      content.style.maxHeight = null;
      trigger.setAttribute('aria-expanded', 'false')
    } else {
      content.style.maxHeight = content.scrollHeight + "px";
      trigger.setAttribute('aria-expanded', 'true')
    }
  }

  onWindowResize() {
    if (this._resizeRaf) return
    this._resizeRaf = requestAnimationFrame(() => {
      this._resizeRaf = null
      this.updateOpenAccordionsHeight()
    })
  }

  updateOpenAccordionsHeight() {
    this.accordions.forEach((accordion) => {
      const trigger = accordion.querySelector(selectors.accordionTrigger)
      const content = accordion.querySelector(selectors.accordionContent)
      if (!trigger || !content) return
      if (!trigger.classList.contains(classes.active)) return
      content.style.maxHeight = content.scrollHeight + 'px'
    })
  }

  initContentObservers() {
    if (typeof ResizeObserver !== 'function') return

    this.accordions.forEach((accordion) => {
      const trigger = accordion.querySelector(selectors.accordionTrigger)
      const content = accordion.querySelector(selectors.accordionContent)
      if (!trigger || !content) return
      if (this._contentObservers.has(content)) return

      const observer = new ResizeObserver(() => {
        if (!this.isConnected) return
        if (!trigger.classList.contains(classes.active)) return
        content.style.maxHeight = content.scrollHeight + 'px'
      })
      observer.observe(content)
      this._contentObservers.set(content, observer)
    })
  }

  destroyContentObservers() {
    this._contentObservers.forEach((observer) => observer.disconnect())
    this._contentObservers.clear()
  }

  destroyTriggerHandlers() {
    this._triggerHandlers.forEach((handler, trigger) => {
      trigger.removeEventListener('click', handler)
    })
    this._triggerHandlers.clear()
  }
}

if (!customElements.get('core-accordion')) {
  customElements.define('core-accordion', CoreAccordion);
}
